import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Button from '@material-ui/core/Button';
import InputLabel from '@material-ui/core/InputLabel';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import PersonIcon from '@material-ui/icons/Person';
import TextField from '@material-ui/core/TextField';
import moment from 'moment';
import {
  useFetchCancellationReasonsOptions,
  useFetchCancellingPartiesOptions,
} from '../../../../actions/areas/planner/fetchHooks';
import useTimeline from '../../timeline/useTimeline';
import globals from '../../../../globals/communityGlobals';
import { cancelTreatmentInstance } from '../../../../actions/entities';
import _ from '../../../../utils/lodash';
import KeyboardDateSelector from '../../../../components/common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import KeyboardTimeSelector from '../../../../components/common/KeyboardDateTimeSelectors/KeyboardTimeSelector';
import { getErrorProps, checkRequiredOnBlur } from '../../../../utils/keyboardSelectorFunctions';
import { isDateInPastOrToday, isValidDate } from '../../../../utils/dates';

const errors = globals.getArea('error');

export default function CancelTreatment({ treatmentDetails, toggleRefresh, includeTreatments }) {
  const [state, setState] = useState({
    selectedReason: null,
    cancelledBy: null,
    cancellingParty: null,
    cancellationName: null,
    riskAssesment: null,
    selectedCancellingNoticeDate: new Date(globals.now),
  });
  const settingList = useSelector(store => store.global.auth.settings);
  const [dateTime, setDateTime] = useState({
    selectedCancellingNoticeDate: new Date(globals.now),
    selectedCancellingNoticeTime: new Date(globals.now),
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [timeDifference, setTimeDifference] = useState({
    days: 0,
    hours: 0,
  });

  if (!treatmentDetails) return null;

  const earliestTimeSlot = treatmentDetails.treatmentInstance.firstPossibleTimeForExecution;

  const showNotice = () => {
    const selectedDateTime = `${moment(dateTime.selectedCancellingNoticeDate).format(
      globals.dateFormats.date,
    )}T${moment(dateTime.selectedCancellingNoticeTime).format(globals.timeFormats.long)}`;
    const noticePeriod = moment(earliestTimeSlot).diff(selectedDateTime);
    let calcRestDaysPeriod = moment(earliestTimeSlot).diff(selectedDateTime, 'days');
    let calcTimePeriod = moment.duration(noticePeriod).hours();
    const calcMinPeriod = moment.duration(noticePeriod).minutes();
    if (calcMinPeriod > 0) {
      calcTimePeriod += 1;
    }
    if (calcTimePeriod === 24) {
      calcRestDaysPeriod += 1;
      calcTimePeriod = 0;
    }
    setTimeDifference({ days: calcRestDaysPeriod, hours: calcTimePeriod });
    setState({
      ...state,
      selectedCancellingNoticeDate: moment(selectedDateTime).format(globals.dateFormats.localApiFormat),
    });
  };

  useEffect(() => {
    showNotice();
  }, [dateTime.selectedCancellingNoticeDate, dateTime.selectedCancellingNoticeTime]);

  const dispatch = useDispatch();
  const extendedCancelProcessSetting = _.find(settingList, 'ExtendedCancellationProcess');
  const isExtendedCancellationProcessTrue = _.values(extendedCancelProcessSetting)[0];

  let cancellingPartiesOptions = [];
  // eslint-disable-next-line no-unused-expressions
  isExtendedCancellationProcessTrue === 'true'
    ? ({ data: cancellingPartiesOptions } = useFetchCancellingPartiesOptions(false))
    : null;

  const { data: cancellationReasonsOptions } = useFetchCancellationReasonsOptions(false);

  const {
    activeUnassignedTreatmentForCancel,
    setActiveUnassignedTreatmentForCancel,
    refreshClinicianTreatmentsAndClosePanels,
    shouldResetCancelTreatmentInstance,
  } = useTimeline();
  const locale = globals.getArea('treatments');
  const errorsLocale = globals.getArea('error');
  const riskMaxLength = 500;
  const cancelNameMaxLength = 50;

  const extendedCancellationValidation =
    isExtendedCancellationProcessTrue === 'true' &&
    (state.cancelledBy === null || (state.cancellationName && state.cancellationName.length > cancelNameMaxLength));

  const noValidationErrors = () => {
    return (
      isValidDate(dateTime.selectedCancellingNoticeDate) &&
      isValidDate(dateTime.selectedCancellingNoticeTime) &&
      !validationErrors.selectedCancellingNoticeDate &&
      !validationErrors.selectedCancellingNoticeTime
    );
  };

  const isDisabledSave = () => {
    return (
      !state.selectedReason ||
      !noValidationErrors() ||
      !isDateInPastOrToday(dateTime.selectedCancellingNoticeDate) ||
      !state.riskAssesment ||
      state.riskAssesment.length > riskMaxLength ||
      extendedCancellationValidation
    );
  };

  const cancelTreatment = () => {
    dispatch(
      cancelTreatmentInstance(
        activeUnassignedTreatmentForCancel.treatmentId,
        activeUnassignedTreatmentForCancel.treatmentInstanceId !== null
          ? activeUnassignedTreatmentForCancel.treatmentInstanceId
          : -1,
        state.selectedReason,
        state.cancelledBy,
        state.cancellingParty,
        state.cancellationName,
        state.selectedCancellingNoticeDate,
        state.riskAssesment,
        treatmentDetails.treatmentInstance.occurrence,
        treatmentDetails.treatmentInstance.effectiveTimeSlots,
      ),
    ).then(() => {
      shouldResetCancelTreatmentInstance(true);
      refreshClinicianTreatmentsAndClosePanels();
      if (includeTreatments) toggleRefresh();
    });
  };

  const visibilityClass = classNames({
    'fields-should-be-visible': isExtendedCancellationProcessTrue === 'true',
    'fields-should-be-hidden': isExtendedCancellationProcessTrue === 'false',
  });

  const headerInfo = () => {
    return (
      <div className="grey-sidePanel-header">
        <div className="sidePanel-header-subtitle">
          <Box flexGrow={1} py={1}>
            <Typography variant="subtitle1">Cancel visit</Typography>
          </Box>
        </div>
        <div className="sidePanel-header-title-wrap">
          <div className="sidePanel-header-title">
            <Typography variant="h2">{activeUnassignedTreatmentForCancel.patientName}</Typography>
            <Typography variant="h3" gutterBottom>
              {activeUnassignedTreatmentForCancel.postcode}
            </Typography>
            <Typography variant="h5">{activeUnassignedTreatmentForCancel.band}</Typography>
          </div>
          <div className="sidePanel-header-info">
            <div className="sidePanel-header-info patient-header">
              <List component="nav">
                <div className="patient-info-details">
                  <ListItem key={'required-people'}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText primary={activeUnassignedTreatmentForCancel.numberOfClinicians} />
                  </ListItem>
                </div>
              </List>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleDateTimeChange = (value, fieldName) => {
    setValidationErrors(prevState => ({
      ...prevState,
      [fieldName]: value ? null : errors.getMsg('requiredFieldValidationMessage'),
    }));

    setDateTime(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const mainContent = () => {
    return (
      <div className="copy-sidePanel-form">
        <Typography variant="h6" gutterBottom>
          {`${locale.getMsg('cancellationReason')} *`}
        </Typography>
        <Select multiple={false} onChange={e => setState({ ...state, selectedReason: e.target.value })}>
          {cancellationReasonsOptions &&
            cancellationReasonsOptions.map(cancelReason => (
              <MenuItem key={cancelReason.id} value={cancelReason.id}>
                {cancelReason.name}
              </MenuItem>
            ))}
        </Select>
        <div className={visibilityClass}>
          <Typography variant="h6" gutterBottom>
            {`${locale.getMsg('cancelledBy')} *`}
          </Typography>
          <Select multiple={false} onChange={e => setState({ ...state, cancelledBy: e.target.value })}>
            <MenuItem value={0}>Internal</MenuItem>
            <MenuItem value={1}>External</MenuItem>
          </Select>
          <Typography variant="h6" gutterBottom>
            {`${locale.getMsg('cancellingParty')}`}
          </Typography>
          <Select multiple={false} onChange={e => setState({ ...state, cancellingParty: e.target.value })}>
            {cancellingPartiesOptions &&
              cancellingPartiesOptions.map(party => (
                <MenuItem key={party.id} value={party.id}>
                  {party.name}
                </MenuItem>
              ))}
          </Select>
          <Typography variant="h6" gutterBottom>
            {`${locale.getMsg('cancellingName')}`}
          </Typography>
          <TextField
            multiline
            value={state.cancellationName}
            error={state.cancellationName && state.cancellationName.length > cancelNameMaxLength}
            helperText={errorsLocale.getMsg('validationMaxLength', [`${cancelNameMaxLength}`])}
            onChange={e => setState({ ...state, cancellationName: e.target.value })}
          />
          <Typography variant="h6" gutterBottom>
            {`${locale.getMsg('cancellingDate')} *`}
          </Typography>
          <Box display="flex" flexDirection="row" alignItems="baseline">
            <Box
              p={1}
              flexGrow={1}
              style={{ width: '30%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <KeyboardDateSelector
                id="date-picker-inline"
                required
                value={dateTime.selectedCancellingNoticeDate}
                onChange={date => handleDateTimeChange(date, 'selectedCancellingNoticeDate')}
                onBlur={event =>
                  checkRequiredOnBlur(
                    event.target.value,
                    setValidationErrors,
                    'selectedCancellingNoticeDate',
                    validationErrors,
                  )
                }
                errorProps={getErrorProps(validationErrors.selectedCancellingNoticeDate)}
                disableFuture
              />
              <InputLabel
                style={{ color: '#999999', fontSize: '10px' }}
              >{`Notice period: ${timeDifference.days} days, ${timeDifference.hours} hours`}</InputLabel>
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '28%', display: 'flex', alignItems: 'center' }}>
              <KeyboardTimeSelector
                id="time-picker"
                required
                value={dateTime.selectedCancellingNoticeTime}
                onChange={time => handleDateTimeChange(time, 'selectedCancellingNoticeTime')}
                onBlur={event =>
                  checkRequiredOnBlur(
                    event.target.value,
                    setValidationErrors,
                    'selectedCancellingNoticeTime',
                    validationErrors,
                  )
                }
                errorProps={getErrorProps(validationErrors.selectedCancellingNoticeTime)}
              />
            </Box>
          </Box>
        </div>
        <Typography variant="h6" gutterBottom>
          {`${locale.getMsg('riskAssessment')} *`}
        </Typography>
        <TextField
          required
          multiline
          value={state.riskAssesment}
          error={state.riskAssesment && state.riskAssesment.length > riskMaxLength}
          helperText={errorsLocale.getMsg('validationMaxLength', [`${riskMaxLength}`])}
          onChange={e => setState({ ...state, riskAssesment: e.target.value })}
        />
      </div>
    );
  };

  const actionButtons = () => {
    return (
      <div className="sidePanel-footer">
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setActiveUnassignedTreatmentForCancel(null);
              }}
            >
              Back
            </Button>
          </div>
          <div className="sidePanel-deactivateButton">
            <Button variant="contained" color="primary" disabled={isDisabledSave()} onClick={cancelTreatment}>
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer
        anchor="right"
        open={activeUnassignedTreatmentForCancel !== null}
        onOpen={() => {}}
        onClose={() => setActiveUnassignedTreatmentForCancel(null)}
      >
        <div className="sidePanel-wrapper" style={{ width: '350px' }} id="cancel-side-panel">
          {headerInfo()}
          {mainContent()}
          {actionButtons()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
